/**
 * Auth state machine — pure module-scoped state, no React-specific
 * machinery. The React provider in `auth/provider.tsx` subscribes via
 * `subscribe(...)` and re-renders when the snapshot changes; the
 * Tanstack Query interceptor (`api/client.ts`) reads the snapshot
 * synchronously to attach `Authorization: Bearer ...` headers.
 *
 * State machine (per chip plan Phase 2):
 *
 *   unauthenticated → authenticating → authenticated
 *                                ↓                       ↑
 *                              error                refreshing
 *                                                       ↑
 *                                                    expired
 *
 * - `unauthenticated`: no token. Login redirect required.
 * - `authenticating`: PKCE round-trip in flight (login init or callback exchange).
 * - `authenticated`: valid `TokenSet` in memory.
 * - `refreshing`: silent refresh of a near-expiry token in flight.
 * - `expired`: refresh failed; consumer must re-authenticate.
 * - `error`: hard auth failure (malformed callback, exchange 4xx/5xx).
 *
 * Two additions for docs#239 finding 41:
 *
 * - `unauthenticated → refreshing` through `startRestoring()`: a full page load tries the
 *   httpOnly refresh cookie once before sending the browser to IAM.
 * - `sessionEnding`: an `authenticated` snapshot whose background refresh was refused. The
 *   access token still works until it expires, and the provider warns the user meanwhile.
 */

import type { DecodedClaims, TokenSet } from "@/auth/tokens";
import { decodeJwtPayload } from "@/auth/tokens";

export type AuthStatus =
  | "unauthenticated"
  | "authenticating"
  | "authenticated"
  | "refreshing"
  | "expired"
  | "error";

export interface AuthSnapshot {
  status: AuthStatus;
  tokens: TokenSet | null;
  claims: DecodedClaims | null;
  error: string | null;
  /**
   * True when a background refresh was refused while the access token still works. The
   * session then ends at `tokens.expiresAtSec` unless a retry succeeds, and the provider
   * shows a warning with that time. Always false outside `authenticated`.
   */
  sessionEnding: boolean;
}

const INITIAL: AuthSnapshot = {
  status: "unauthenticated",
  tokens: null,
  claims: null,
  error: null,
  sessionEnding: false,
};

let current: AuthSnapshot = INITIAL;
const listeners = new Set<() => void>();

export function getSnapshot(): AuthSnapshot {
  return current;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(): void {
  for (const l of listeners) {
    l();
  }
}

function transition(next: AuthSnapshot): void {
  current = next;
  emit();
}

// ----- Transition helpers --------------------------------------------------

export function startAuthenticating(): void {
  transition({ ...INITIAL, status: "authenticating" });
}

export function setAuthenticated(tokens: TokenSet): void {
  let claims: DecodedClaims | null = null;
  try {
    claims = decodeJwtPayload(tokens.accessToken);
  } catch {
    // Decoding failure shouldn't crash auth — backend is the real validator.
    claims = null;
  }
  transition({
    status: "authenticated",
    tokens,
    claims,
    error: null,
    sessionEnding: false,
  });
}

/**
 * A full page load holds no token. Before sending the browser to IAM, try the refresh
 * cookie once: `unauthenticated → refreshing`. The guards treat `refreshing` as not ready,
 * so nothing redirects while the attempt is in flight. No-op from any other status.
 */
export function startRestoring(): void {
  if (current.status !== "unauthenticated") {
    return;
  }
  transition({ ...INITIAL, status: "refreshing" });
}

/**
 * A background refresh was refused but the access token has not expired. Stay
 * `authenticated`, so nothing on the page unmounts, and flag the coming end of the session.
 * No-op outside `authenticated`.
 */
export function markSessionEnding(): void {
  if (current.status !== "authenticated" || current.sessionEnding) {
    return;
  }
  transition({ ...current, sessionEnding: true });
}

export function startRefreshing(): void {
  if (current.status !== "authenticated" && current.status !== "expired") {
    return;
  }
  transition({ ...current, status: "refreshing" });
}

export function setExpired(): void {
  transition({
    status: "expired",
    tokens: null,
    claims: null,
    error: null,
    sessionEnding: false,
  });
}

export function setError(message: string): void {
  transition({
    status: "error",
    tokens: null,
    claims: null,
    error: message,
    sessionEnding: false,
  });
}

export function setUnauthenticated(): void {
  transition(INITIAL);
}

/** Test/teardown helper. Production code uses `setUnauthenticated()` instead. */
export function _resetForTests(): void {
  current = INITIAL;
  emit();
}
